import { UntypedFormGroup } from "@angular/forms";
import _, { orderBy } from "lodash";
import { IProduktdefinition } from "../../models/IProduktdefinition";
import { Diameterklass } from "./Diameterklass";
import { Langdklass } from "./Langdklass";
import { Produktmatris } from "./Produktmatris";
import { RadprisJustering } from "./RadprisJustering";

export class ProduktmatrisGrid {
    diameterklasser: Diameterklass[];
    langdklasser: Langdklass[];
    onGridHeadersModified: (() => void)[] = [];
    private prisVm: { [key: string]: Produktmatris } = {};

    // Matrisen har stöd för att skicka med "replicas"
    // Syftet är att stödja masseditering, så om man inte massediterar så är det bara en tom array.
    // Vid masseditering så kopieras de händelser som utförs på matrisen öven på "replicas"
    // MEN: Det är endast händelser som påverkar dimensionerna (dvs lägg till/ta bort/flytta kolumner/rader).
    // Syftet med detta är att kunna upprätthålla logik som beror på dimensionerna, t.ex. arv av värden.
    // Övriga värden (begränsning, pris, radpris, radprisjustering)
    // kopieras endast över vid själva sparningen på DTO-nivå.
    constructor(
        model: IProduktdefinition,
        private readonly form: UntypedFormGroup,
        private readonly defaultRadprisValue: () => number,
        private readonly replicas: ProduktmatrisGrid[] = []) {
        model.produktmatris.forEach(x => {
            this.addCell(new Produktmatris(x));
        });

        this.diameterklasser = orderBy(model.diameterklass.map(x => new Diameterklass(x)), p => p.startdiameter);
        this.langdklasser = orderBy(model.langdklass.map(x => new Langdklass(x)), p => p.startlangd);
    }

    addRow = (newLength: number) => {
        const addRadprisjustering = (diameterklass: Diameterklass, lk: Langdklass) => {
            if (diameterklass.radprisJustering.length > 0) {
                // Om det finns radprisjustering så lägger vi till nya längden
                diameterklass.radprisJustering.push(new RadprisJustering({ startlangd: lk.startlangd,
                    justering: this.defaultRadprisValue() }, true));
            }
        };

        if (this.langdklasser.some(p => p.startlangd === newLength)) {
            return;
        }

        const inheritFrom = _.findLast(this.langdklasser, x => x.startlangd < newLength);
        const langdklass = new Langdklass({
            startlangd: newLength,
            overmal: 0,
            stolpMaxRotdiameter: 0,
            stolpMinRotdiameter: 0
        });

        if (inheritFrom) {
            langdklass.overmal = inheritFrom.overmal;
            langdklass.stolpMaxRotdiameter = inheritFrom.stolpMaxRotdiameter;
            langdklass.stolpMinRotdiameter = inheritFrom.stolpMinRotdiameter;

            // Ärv produktmatris från tidigare rad
            this.diameterklasser.forEach(diameterklass => {
                const pmData = this.getCell(inheritFrom.startlangd, diameterklass.startdiameter);
                const newPmData = Produktmatris.createNew(diameterklass.startdiameter, langdklass.startlangd);
                newPmData.inheritLangd(pmData);
                this.addCell(newPmData);
                addRadprisjustering(diameterklass, langdklass);
            });
        } else {
            // Skapa ny rad om ingen finns att ärva från
            this.diameterklasser.forEach(diameterklass => {
                this.addCell(Produktmatris.createNew(diameterklass.startdiameter, langdklass.startlangd));
                addRadprisjustering(diameterklass, langdklass);
            });
        }
        this.langdklasser.push(langdklass);
        this.langdklasser = _.sortBy(this.langdklasser, o => o.startlangd);
        this.form.markAsDirty();
        this.onGridHeadersModified.forEach(x => x());
        this.replicas.forEach(x => x.addRow(newLength));
    };

    deleteRow = (currentLength: number) => {
        const lastIndex = this.langdklasser.findIndex(p => p.startlangd === currentLength);
        this.langdklasser.splice(lastIndex, 1);
        this.langdklasser = _.sortBy(this.langdklasser, o => o.startlangd);
        this.diameterklasser.forEach(x => {
            this.deleteCell(currentLength, x.startdiameter);
            _.remove(x.radprisJustering, p => p.startlangd === currentLength);
        });
        this.form.markAsDirty();
        this.onGridHeadersModified.forEach(x => x());
        this.replicas.forEach(x => x.deleteRow(currentLength));
    };

    updateRow = (oldLength: number, newLength: any) => {
        this.langdklasser.find(p => p.startlangd === oldLength).startlangd = newLength;
        this.diameterklasser.forEach(diameterklass => {
            this.moveCell(oldLength, diameterklass.startdiameter, newLength, diameterklass.startdiameter);
            const oldJustering = _.find(diameterklass.radprisJustering, x => x.startlangd === oldLength);
            if (oldJustering) {
                oldJustering.startlangd = newLength;
            }
        });
        this.langdklasser = _.sortBy(this.langdklasser, o => o.startlangd);
        this.form.markAsDirty();
        this.onGridHeadersModified.forEach(x => x());
        this.replicas.forEach(x => x.updateRow(oldLength, newLength));
    };

    updateColumn = (oldDiameter: number, newDiameter: any) => {
        this.diameterklasser.find(p => p.startdiameter === oldDiameter).startdiameter = newDiameter;
        this.langdklasser.forEach(langdklass => {
            this.moveCell(langdklass.startlangd, oldDiameter, langdklass.startlangd, newDiameter);
        });
        this.diameterklasser = _.sortBy(this.diameterklasser, o => o.startdiameter);
        this.form.markAsDirty();
        this.onGridHeadersModified.forEach(x => x());
        this.replicas.forEach(x => x.updateColumn(oldDiameter, newDiameter));
    };

    clearGrid() {
        this.langdklasser.splice(0, this.langdklasser.length);
        this.diameterklasser.splice(0, this.diameterklasser.length);
        this.prisVm = {};
        this.form.markAsDirty();
        this.onGridHeadersModified.forEach(x => x());
        this.replicas.forEach(x => x.clearGrid());
    }

    copyRadpriser = (sourceGrid: ProduktmatrisGrid) => {
        this.diameterklasser.forEach(diameterklass => {
            const srcDiameterklass = sourceGrid.getDiameterklass(diameterklass.startdiameter);
            diameterklass.radpris = srcDiameterklass.radpris;
            diameterklass.radprisJustering = srcDiameterklass.radprisJustering;
        });
    };

    copyVardematrisProperties(sourceGrid: ProduktmatrisGrid) {
        sourceGrid.getCells().forEach(cell => this.getCell(cell.startlangd, cell.startdiameter).inheritMassediteringProperties(cell));
    }

    clearRadprisjusteringar = (startdiameter: number) => {
        const diameterklass = this.diameterklasser.find(s => s.startdiameter === startdiameter);
        if (diameterklass) {
            diameterklass.radprisJustering.splice(0, diameterklass.radprisJustering.length);
        }

        this.replicas.forEach(x => x.clearRadprisjusteringar(startdiameter));
    };

    addRadprisjustering = (startdiameter: any) => {
        const diameterklass = this.diameterklasser.find(y => y.startdiameter === startdiameter);
        this.langdklasser.forEach(lk => {
            diameterklass.radprisJustering.push(new RadprisJustering({ startlangd: lk.startlangd,
                justering: this.defaultRadprisValue() }, true));
        });

        this.replicas.forEach(x => x.addRadprisjustering(startdiameter));
    };

    moveRadprisjustering = (oldDiameter: number, newDiameter: any) => {
        const oldDiam = this.diameterklasser.find(y => y.startdiameter === oldDiameter);
        const newDiam = this.diameterklasser.find(y => y.startdiameter === newDiameter);
        newDiam.radprisJustering.push(...oldDiam.radprisJustering);
        oldDiam.radprisJustering.splice(0, oldDiam.radprisJustering.length);

        this.replicas.forEach(x => x.moveRadprisjustering(oldDiameter, newDiameter));
    };

    getDiameterklass = (startdiameter: number): Diameterklass => this.diameterklasser.find(x => x.startdiameter === startdiameter);

    addCell = (produktmatris: Produktmatris) => {
        this.prisVm[produktmatris.key] = produktmatris;
    };

    addColumn = (newDiameter: any) => {
        if (this.diameterklasser.some(s => s.startdiameter === newDiameter)) {
            return;
        }

        // Hitta första tidigare längdklass
        const inheritFrom = _.findLast(this.diameterklasser, x => x.startdiameter < newDiameter);
        const diameterklass = new Diameterklass(
            {
                startdiameter: newDiameter,
                radprisJustering: [],
                radpris: 0
            },
            true);

        if (inheritFrom) {
            // Ärv produktmatris från tidigare kolumn
            this.langdklasser.forEach(langdklass => {
                const pmData = this.getCell(langdklass.startlangd, inheritFrom.startdiameter);
                const newPmData = Produktmatris.createNew(diameterklass.startdiameter, langdklass.startlangd);
                newPmData.inheritDiameter(pmData);
                this.addCell(newPmData);
            });
            diameterklass.radpris = inheritFrom.radpris;
        } else {
            // Skapa ny rad om ingen finns att ärva från
            this.langdklasser.forEach(langdklass => {
                this.addCell(Produktmatris.createNew(diameterklass.startdiameter, langdklass.startlangd));
            });
        }
        this.diameterklasser.push(diameterklass);
        this.diameterklasser = _.sortBy(this.diameterklasser, o => o.startdiameter);
        this.form.markAsDirty();
        this.onGridHeadersModified.forEach(x => x());
        this.replicas.forEach(x => x.addColumn(newDiameter));
    };

    deleteColumn = (oldDiameter: number) => {
        const lastIndex = this.diameterklasser.findIndex(p => p.startdiameter === oldDiameter);
        this.diameterklasser.splice(lastIndex, 1);
        this.langdklasser.forEach(x => this.deleteCell(x.startlangd, oldDiameter));
        this.form.markAsDirty();
        this.onGridHeadersModified.forEach(x => x());
        this.replicas.forEach(x => x.deleteColumn(oldDiameter));
    };

    getCell = (startlangd: number, startdiameter: number) => this.prisVm[Produktmatris.getKey(startlangd, startdiameter)];

    getCells = () => Object.keys(this.prisVm).map(key => this.prisVm[key]);

    private readonly deleteCell = (startlangd: number, startdiameter: number) => {
        delete this.prisVm[Produktmatris.getKey(startlangd, startdiameter)];
    };

    private readonly moveCell = (oldLength: number, oldDiameter: number, newLength: number, newDiameter: any) => {
        if (oldLength === newLength && oldDiameter === newDiameter) {
            return;
        }
        const cell = this.getCell(oldLength, oldDiameter);
        cell.startlangd = newLength;
        cell.startdiameter = newDiameter;
        cell.markAsDirty();
        this.prisVm[cell.key] = cell;
        this.form.markAsDirty();
        this.deleteCell(oldLength, oldDiameter);
    };
}
